import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MinistryTransferRequest, TransferRequestStatus } from '../models/unit.model';
import { UnitService } from '../services/unit.service';

type StatusFilter = TransferRequestStatus | 'All';

@Component({
  selector: 'app-ministry-transfer-requests-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './ministry-transfer-requests-page.component.html',
  styleUrl: './ministry-transfer-requests-page.component.scss',
})
export class MinistryTransferRequestsPageComponent implements OnInit, OnDestroy {
  requests: MinistryTransferRequest[] = [];
  statusFilter: StatusFilter = 'Pending';
  isLoading = false;
  errorMessage: string | null = null;
  resolvingRequestId: string | null = null;

  readonly displayedColumns = [
    'requestedDate',
    'person',
    'ministry',
    'route',
    'status',
    'resolvedDate',
    'notes',
    'actions',
  ];

  private readonly destroy$ = new Subject<void>();
  private loadRequestId = 0;

  constructor(
    private readonly unitService: UnitService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadRequests();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStatusFilterChange(value: StatusFilter | undefined): void {
    if (!value || value === this.statusFilter) {
      return;
    }

    this.statusFilter = value;
    this.loadRequests();
  }

  loadRequests(): void {
    const currentLoadId = ++this.loadRequestId;
    this.isLoading = true;
    this.errorMessage = null;
    const status = this.statusFilter === 'All' ? undefined : this.statusFilter;

    this.unitService.getAllMinistryTransferRequests(status).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        if (currentLoadId === this.loadRequestId) {
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      })
    ).subscribe({
      next: requests => {
        this.requests = requests;
      },
      error: () => {
        this.requests = [];
        this.errorMessage = 'No se pudieron cargar los pases ministeriales.';
      },
    });
  }

  approve(request: MinistryTransferRequest): void {
    this.resolve(request, 'approve');
  }

  reject(request: MinistryTransferRequest): void {
    this.resolve(request, 'reject');
  }

  navigateToPerson(personId: string): void {
    this.router.navigate(['/people', personId]);
  }

  navigateToUnit(unitId: string): void {
    this.router.navigate(['/units', unitId]);
  }

  getPersonName(request: MinistryTransferRequest): string {
    return `${request.personFirstName} ${request.personLastName}`.trim();
  }

  getStatusLabel(status: TransferRequestStatus): string {
    const labels: Record<TransferRequestStatus, string> = {
      Pending: 'Pendiente',
      Approved: 'Aprobado',
      Rejected: 'Rechazado',
      Cancelled: 'Cancelado',
    };

    return labels[status];
  }

  private resolve(request: MinistryTransferRequest, action: 'approve' | 'reject'): void {
    if (request.status !== 'Pending' || this.resolvingRequestId) {
      return;
    }

    this.resolvingRequestId = request.id;
    const operation$ = action === 'approve'
      ? this.unitService.approveMinistryTransferRequest(request.id)
      : this.unitService.rejectMinistryTransferRequest(request.id);

    operation$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.resolvingRequestId = null;
        this.loadRequests();
      },
      error: () => {
        this.resolvingRequestId = null;
        this.errorMessage = action === 'approve'
          ? 'No se pudo aprobar el pase ministerial.'
          : 'No se pudo rechazar el pase ministerial.';
      },
    });
  }
}
